import { colors } from '../tokens/colors';
import { semantic as baseSemantic } from '../tokens/semantic';
import { buildChatMessageTokens } from '../tokens/chatMessage';
import { typography } from '../tokens/typography';
import { derivePrimaryScale, hexToRgba } from './colorUtils';

function buildInputs(mode, surfaces, borders, interactive) {
  return {
    radius: '8px',
    background: surfaces.surface,
    backgroundHover: surfaces.surface,
    backgroundFocus: surfaces.surface,
    backgroundDisabled:
      mode === 'dark' ? 'rgba(255,255,255,0.04)' : colors.grey[100],
    fontSize: typography.fontSizeMd,
    controlHeight: 40,
    paddingY: 10,
    paddingX: 12,
    cursorDisabled: 'not-allowed',
    border: borders.border,
    focusRing: borders.focusRing,
  };
}

function buildStates(mode) {
  const alpha = mode === 'dark' ? 0.18 : 1;
  if (mode === 'dark') {
    return {
      success: '#34d399',
      successSoft: hexToRgba('#34d399', alpha),
      warning: '#fbbf24',
      warningSoft: hexToRgba('#fbbf24', alpha),
      danger: '#f87171',
      dangerSoft: hexToRgba('#f87171', alpha),
      info: '#60a5fa',
      infoSoft: hexToRgba('#60a5fa', alpha),
    };
  }
  return { ...baseSemantic.states };
}

function buildStatus(mode) {
  if (mode === 'dark') {
    return {
      expired: '#fca5a5',
      expiredBorder: hexToRgba('#fca5a5', 0.35),
      expiredBg: hexToRgba('#fca5a5', 0.12),
      warning: '#fbbf24',
      warningText: '#fde68a',
      warningAction: '#fcd34d',
      warningSurface: hexToRgba('#fbbf24', 0.12),
      warningBorder: hexToRgba('#fbbf24', 0.28),
    };
  }
  return { ...baseSemantic.status };
}

function buildAttachMenu(mode, chatPrimary) {
  const soft = (hex, alphaLight, alphaDark) =>
    mode === 'dark' ? hexToRgba(hex, alphaDark) : hexToRgba(hex, alphaLight);

  return {
    template: { color: chatPrimary, bg: soft(chatPrimary, 0.08, 0.16) },
    document: { color: '#7C5CBF', bg: soft('#7C5CBF', 0.08, 0.16) },
    photo: { color: '#3B82C4', bg: soft('#3B82C4', 0.08, 0.16) },
    voice: { color: '#E07B54', bg: soft('#E07B54', 0.08, 0.16) },
  };
}

function buildTable(mode, surfaces, borders, text, interactive) {
  const surfaceShadow =
    mode === 'dark'
      ? '0 1px 2px rgba(0, 0, 0, 0.24)'
      : '0 1px 3px rgba(16, 24, 40, 0.08), 0 1px 2px rgba(16, 24, 40, 0.04)';

  return {
    background: surfaces.surface,
    headerBackground: mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#F9FAFB',
    headerText: text.primary,
    rowBackground: surfaces.surface,
    rowHoverBackground: mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#F9FAFB',
    borderColor: borders.border,
    sortIconColor: mode === 'dark' ? 'rgba(255,255,255,0.45)' : '#98A2B3',
    rowHeight: 56,
    cellPaddingX: 20,
    surfaceShadow,
    paginationActiveBg: interactive.primary,
    paginationActiveText: interactive.primaryForeground,
  };
}

function buildSidebar(mode) {
  if (mode === 'dark') {
    return {
      background: '#0f172a',
      backgroundEnd: '#020617',
      foreground: colors.white,
      muted: 'rgba(255,255,255,0.72)',
      border: 'rgba(255,255,255,0.12)',
      hover: 'rgba(255,255,255,0.08)',
      active: 'rgba(255,255,255,0.14)',
      activeBorder: 'rgba(255,255,255,0.28)',
    };
  }
  return { ...baseSemantic.sidebar };
}

function buildAppShell(mode, surfaces, text, borders, chatPrimary) {
  const sidebarBase = baseSemantic.appShell.sidebar;
  const accent = chatPrimary || sidebarBase.accent;

  return {
    sidebar: {
      ...sidebarBase,
      accent,
      accentSoft: hexToRgba(accent, 0.16),
      accentGlow: hexToRgba(accent, 0.35),
      toggleBorder: 'rgba(0,0,0,0.06)',
      toggleIcon: mode === 'dark' ? '#71717a' : '#71717a',
    },
    header: {
      background: surfaces.surface,
      text: text.primary,
      textMuted: text.muted,
      border: borders.border,
      borderSubtle:
        mode === 'dark' ? 'rgba(255,255,255,0.06)' : baseSemantic.appShell.header.borderSubtle,
      shadow: mode === 'dark' ? '0 1px 2px rgba(0,0,0,0.4)' : baseSemantic.appShell.header.shadow,
      testShadow: mode === 'dark' ? '0 1px 2px rgba(0,0,0,0.3)' : baseSemantic.appShell.header.testShadow,
    },
    shell: {
      background: surfaces.background,
      mobileMenuButtonBg: surfaces.surface,
    },
  };
}

export function buildModeAwareSemantic({
  mode,
  customPalette,
  brandMain,
  brandScale,
  chatMain,
  chatScale,
}) {
  const resolvedBrandMain = brandMain || customPalette.primary?.main;
  const resolvedBrandScale =
    brandScale || derivePrimaryScale(resolvedBrandMain, mode);
  const resolvedChatMain = chatMain;
  const resolvedChatScale = chatScale || derivePrimaryScale(resolvedChatMain, mode);

  const surfaceBackground =
    customPalette.background?.default ||
    (mode === 'dark' ? '#0b1220' : colors.grey[50]);
  const surfacePaper =
    customPalette.background?.paper ||
    (mode === 'dark' ? '#111827' : colors.white);
  const textPrimary = customPalette.text?.primary;
  const textSecondary = customPalette.text?.secondary;
  const textMuted = customPalette.text?.disabled;
  const divider = customPalette.divider;

  const surfaces = {
    background: surfaceBackground,
    surface: surfacePaper,
    surfaceSubtle: mode === 'dark' ? colors.grey[900] : colors.grey[50],
    surfaceMuted: mode === 'dark' ? 'rgba(255,255,255,0.04)' : colors.grey[100],
    surfaceElevated: surfacePaper,
  };

  const interactive = {
    ...baseSemantic.interactive,
    primary: resolvedBrandScale.main,
    primaryHover: resolvedBrandScale.hover,
    primaryActive: resolvedBrandScale.active,
    primarySoft: resolvedBrandScale.soft,
    primaryForeground: resolvedBrandScale.contrastText,
    primaryBorder: resolvedBrandScale.border,
    primaryMuted: resolvedBrandScale.muted,
  };

  const borders = {
    border: divider,
    borderStrong: mode === 'dark' ? 'rgba(255,255,255,0.16)' : colors.grey[400],
    focusRing: hexToRgba(resolvedBrandScale.main, 0.22),
  };

  const chat = {
    ...baseSemantic.chat,
    primary: resolvedChatScale.main,
    primaryLight: resolvedChatScale.light,
    primaryDark: resolvedChatScale.dark,
    primarySurface: resolvedChatScale.soft,
    primaryBorder: resolvedChatScale.border,
    primaryMuted: resolvedChatScale.muted,
    primaryContrast: resolvedChatScale.contrastText,
    secondary:
      mode === 'dark'
        ? hexToRgba(resolvedChatMain, 0.14)
        : hexToRgba(resolvedChatMain, 0.08),
    secondaryHover:
      mode === 'dark'
        ? hexToRgba(resolvedChatMain, 0.22)
        : hexToRgba(resolvedChatMain, 0.12),
    secondaryText: mode === 'dark' ? resolvedChatScale.light : resolvedChatScale.dark,
    outboundBg: resolvedChatScale.main,
    outboundText: resolvedChatScale.contrastText,
    background: mode === 'dark' ? surfaceBackground : baseSemantic.chat.message?.feed?.background || baseSemantic.chat.background,
    paper: surfacePaper,
    textPrimary,
    textSecondary,
    textMuted,
    divider,
    overlay: mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.7)',
    message: buildChatMessageTokens({
      primaryScale: resolvedChatScale,
      mode,
      textPrimary,
      textMuted,
      paper: surfacePaper,
      surfaceBackground,
    }),
    userList: {
      ...baseSemantic.chat.userList,
      surface: surfacePaper,
      border: divider,
      iconColor: textMuted,
      filterTabsBackground: mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#F0F2F5',
      itemBackground: surfacePaper,
      itemHoverBackground: mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#F0F2F5',
      itemSelectedBackground: mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#F0F2F5',
      itemSelectedBorder: 'transparent',
      itemHoverBorder: 'transparent',
      avatarBackground: mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#EEF2F6',
      avatarSelectedBackground: mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#EEF2F6',
      avatarColor: mode === 'dark' ? colors.white : textPrimary,
      avatarBadgeBorder: surfacePaper,
      avatarGroupBackground:
        mode === 'dark'
          ? hexToRgba(resolvedChatMain, 0.18)
          : hexToRgba(resolvedChatMain, 0.12),
      avatarGroupColor: mode === 'dark' ? resolvedChatScale.light : resolvedChatMain,
      userNameColor: textPrimary,
      descriptionColor: textSecondary,
      metaColor: textMuted,
    },
  };

  const text = {
    primary: textPrimary,
    secondary: textSecondary,
    muted: textMuted,
    inverse: colors.white,
  };

  return {
    ...baseSemantic,
    surfaces,
    text,
    interactive,
    borders,
    inputs: buildInputs(mode, surfaces, borders, interactive),
    table: buildTable(mode, surfaces, borders, text, interactive),
    states: buildStates(mode),
    status: buildStatus(mode),
    attachMenu: buildAttachMenu(mode, resolvedChatScale.main),
    sidebar: buildSidebar(mode),
    appShell: buildAppShell(mode, surfaces, text, borders, resolvedChatScale.main),
    chat,
  };
}
